import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import { useDataCache } from '../../context/DataCacheContext';
import { CACHE_KEYS } from '../../constants/cacheKeys';

interface EditFocusModalProps {
  isOpen: boolean;
  onClose: () => void;
  focus: any; // CareerFocusWithMetrics
  onSuccess?: () => void;
}

const EditFocusModal: React.FC<EditFocusModalProps> = ({
  isOpen,
  onClose,
  focus,
  onSuccess
}) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    company: focus?.company || '',
    role: focus?.role || '',
    start_date: focus?.start_date ? new Date(focus.start_date).toISOString().split('T')[0] : '',
    end_date: focus?.end_date ? new Date(focus.end_date).toISOString().split('T')[0] : ''
  });

  // Update form data when focus changes
  useEffect(() => {
    if (focus) {
      setFormData({
        company: focus.company || '',
        role: focus.role || '',
        start_date: focus.start_date ? new Date(focus.start_date).toISOString().split('T')[0] : '',
        end_date: focus.end_date ? new Date(focus.end_date).toISOString().split('T')[0] : ''
      });
    }
  }, [focus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id || !focus?.id) {
      showToast('Missing required information', 'error');
      return;
    }

    // Validation
    if (!formData.company.trim()) {
      showToast('Company/Organization name is required', 'error');
      return;
    }

    if (!formData.role.trim()) {
      showToast('Role/Title is required', 'error');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      showToast('Start and end dates are required', 'error');
      return;
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update the focus
      const { error } = await supabase
        .from('user_career_focuses')
        .update({
          company: formData.company.trim(),
          role: formData.role.trim(),
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        })
        .eq('id', focus.id)
        .eq('user_id', user.id);

      if (error) throw error;

      showToast('Focus updated successfully', 'success');
      
      // Invalidate relevant caches
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.ALL_ACTIVE_FOCUSES}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS}_${focus.id}`);
      
      onSuccess?.();
      onClose();
      
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Failed to update focus:', error);
      showToast('Failed to update focus', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex items-center justify-between mb-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Edit Focus
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Company/Organization */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company/Organization <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Google"
                      required
                    />
                  </div>

                  {/* Role/Title */}
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role/Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Software Engineering Intern"
                      required
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditFocusModal;